import type { FooterContent } from '@mawkaa/types';
import { Facebook, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';

interface FooterBlockProps {
  content: FooterContent;
  isEditing?: boolean;
}

const socialIcons: Record<string, React.ElementType> = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube,
};

export function FooterBlock({ content, isEditing }: FooterBlockProps) {
  const { logo, description, columns = [], socialLinks, copyright, style = 'simple' } = content;

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand column */}
          <div className="md:col-span-1">
            {logo ? (
              <img src={logo} alt="Logo" className="h-8 mb-4" />
            ) : (
              <span className="text-xl font-bold">Mon Site</span>
            )}
            {description && (
              <p className="text-gray-400 text-sm mt-4">{description}</p>
            )}

            {/* Social links */}
            {socialLinks && (
              <div className="flex gap-4 mt-6">
                {Object.entries(socialLinks).map(([platform, url]) => {
                  if (!url) return null;
                  const Icon = socialIcons[platform];
                  if (!Icon) return null;

                  return (
                    <a
                      key={platform}
                      href={isEditing ? '#' : url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Link columns */}
          {columns.map((column, index) => (
            <div key={index}>
              <h4 className="font-semibold mb-4">{column.title}</h4>
              <ul className="space-y-2">
                {column.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={isEditing ? '#' : link.url}
                      className="text-gray-400 hover:text-white text-sm transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
          {copyright || `© ${new Date().getFullYear()} Tous droits réservés.`}
        </div>
      </div>
    </footer>
  );
}
